import { useRef, useCallback } from "react";

/**
 * Interface for binding focus/blur listeners to form inputs.
 */
export interface IFieldBindingOptions {
  onFocus?: () => void;
  onBlur?: () => void;
}

/**
 * Return interface for the useActiveFieldTracker hook.
 */
export interface IUseActiveFieldTrackerResult {
  /** Mutable ref holding the field name currently being edited. */
  activeFieldRef: React.MutableRefObject<string | null>;
  /** Explicitly sets or clears the field name currently being edited. */
  setEditingField: (fieldName: string | null) => void;
  /** Checks if a specific field is currently being edited by the user. */
  isEditingField: (fieldName: string) => boolean;
  /** Gets the currently active field name, or null. */
  getActiveField: () => string | null;
  /** Creates onFocus/onBlur event handlers for an input component. */
  bindField: (fieldName: string, options?: IFieldBindingOptions) => {
    onFocus: () => void;
    onBlur: () => void;
  };
}

/**
 * Custom React hook that tracks which form field the user is currently editing.
 * Used during auto-save and entity reloads to ensure that saved/reloaded values
 * do not overwrite fields the user is actively modifying, while safely updating
 * all non-editing fields.
 *
 * @returns {IUseActiveFieldTrackerResult} The tracker controls and binding utilities.
 */
export function useActiveFieldTracker(): IUseActiveFieldTrackerResult {
  const activeFieldRef = useRef<string | null>(null);

  const setEditingField = useCallback((fieldName: string | null) => {
    activeFieldRef.current = fieldName;
  }, []);

  const isEditingField = useCallback((fieldName: string): boolean => {
    return activeFieldRef.current === fieldName;
  }, []);

  const getActiveField = useCallback((): string | null => {
    return activeFieldRef.current;
  }, []);

  const bindField = useCallback(
    (fieldName: string, options?: IFieldBindingOptions) => ({
      onFocus: () => {
        activeFieldRef.current = fieldName;
        options?.onFocus?.();
      },
      onBlur: () => {
        if (activeFieldRef.current === fieldName) {
          activeFieldRef.current = null;
        }
        options?.onBlur?.();
      },
    }),
    []
  );

  return {
    activeFieldRef,
    setEditingField,
    isEditingField,
    getActiveField,
    bindField,
  };
}
